import { useEffect, useMemo, useRef, useState } from 'react'
import { formatCurrency } from '@/lib/utils'

type BubbleHolding = {
  id: string
  label: string
  percent: number
  value: number
}

type HoldingsBubbleChartProps = {
  holdings: BubbleHolding[]
  maxBubbles?: number
}

type BubbleLayoutItem = {
  id: string
  label: string
  percentText: string
  size: number
  center: { x: number; y: number }
  outline: boolean
  value: number
  delay: number
  duration: number
}

export function HoldingsBubbleChart({ holdings, maxBubbles = 6 }: HoldingsBubbleChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [])

  const layout = useMemo(() => {
    if (!holdings.length) {
      return []
    }

    const width = size.width > 0 ? size.width : 520
    const height = size.height > 0 ? size.height : 220
    const bubbleHoldings = holdings.slice(0, maxBubbles)
    const moreCount = Math.max(holdings.length - maxBubbles, 0)
    const padding = 8
    const bounds = {
      minX: padding,
      minY: padding,
      maxX: Math.max(width - padding, padding),
      maxY: Math.max(height - padding, padding),
    }

    const minSize = Math.min(56, width * 0.22)
    const maxSize = Math.max(minSize + 20, Math.min(128, width * 0.36))
    const bubbleGap = 14

    const seedKey = bubbleHoldings
      .map((holding) => `${holding.id}-${Math.round(holding.percent * 1000)}`)
      .join('|')
    const seed = hashString(seedKey) ^ Math.round(width * 10) ^ Math.round(height * 10)
    const rng = createRng(seed)

    const items: BubbleLayoutItem[] = []

    const bubbleSize = (percent: number) => {
      const clamped = Math.min(Math.max(percent, 0.01), 0.35)
      const scaled = Math.sqrt(clamped / 0.35)
      return minSize + (maxSize - minSize) * scaled
    }

    const place = (radius: number) => {
      const minX = bounds.minX + radius
      const maxX = bounds.maxX - radius
      const minY = bounds.minY + radius
      const maxY = bounds.maxY - radius

      const attempts = 120
      for (let i = 0; i < attempts; i += 1) {
        const x = randBetween(rng, minX, maxX)
        const y = randBetween(rng, minY, maxY)
        const candidate = { x, y }
        if (
          items.every(
            (item) => distance(item.center, candidate) >= item.size / 2 + radius + bubbleGap
          )
        ) {
          return candidate
        }
      }

      return { x: randBetween(rng, minX, maxX), y: randBetween(rng, minY, maxY) }
    }

    bubbleHoldings.forEach((holding, index) => {
      const size = bubbleSize(holding.percent)
      const center = place(size / 2)
      const delay = randBetween(rng, 0, 0.4)
      const duration = randBetween(rng, 4.5, 7.5)
      const percentText =
        holding.percent >= 0.01
          ? `${(holding.percent * 100).toFixed(1)}%`
          : '<1%'

      items.push({
        id: holding.id,
        label: holding.label,
        percentText,
        size,
        center,
        outline: false,
        value: holding.value,
        delay: delay + index * 0.05,
        duration,
      })
    })

    if (moreCount > 0) {
      const size = bubbleSize(0.06)
      const center = place(size / 2)
      const delay = randBetween(rng, 0.1, 0.5)
      const duration = randBetween(rng, 4.5, 7.5)
      items.push({
        id: `more-${moreCount}`,
        label: `+${moreCount}`,
        percentText: 'more',
        size,
        center,
        outline: true,
        value: 0,
        delay,
        duration,
      })
    }

    return resolveCollisions(snapToContacts(items, bubbleGap, bounds), bubbleGap, bounds)
  }, [holdings, maxBubbles, size.height, size.width])

  if (!holdings.length) {
    return (
      <div className="flex h-56 items-center justify-center rounded-xl border border-dashed border-gray-200 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
        No holdings to visualize yet.
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative h-56 w-full overflow-hidden">
      {layout.map((item) => (
        <div
          key={item.id}
          className="absolute transition-all duration-500"
          style={{ left: item.center.x, top: item.center.y }}
        >
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ width: item.size, height: item.size }}
          >
            <div
              className={`flex h-full w-full items-center justify-center rounded-full shadow-sm ${
                item.outline
                  ? 'border border-primary-200/70 bg-white/70 text-primary-700 dark:border-primary-900/40 dark:bg-gray-900/40 dark:text-primary-200'
                  : 'border border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-900/40 dark:bg-primary-900/30 dark:text-primary-200'
              } animate-bubble`}
              style={{
                animationDelay: `${item.delay}s`,
                animationDuration: `${item.duration}s`,
              }}
            >
              <div
                className="flex h-full w-full flex-col items-center justify-center gap-1 text-center"
                title={`${item.label} - ${formatCurrency(item.value)} (${item.percentText})`}
              >
                <span className="max-w-[70%] truncate text-[11px] font-semibold uppercase tracking-wide">
                  {item.label}
                </span>
                <span className="text-[10px] text-primary-700/70 dark:text-primary-200/70">
                  {item.percentText}
                </span>
              </div>
            </div>
            {item.outline && (
              <div className="pointer-events-none absolute inset-0 rounded-full border border-primary-300/60" />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const snapToContacts = (
  items: BubbleLayoutItem[],
  gap: number,
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
) => {
  const updated = [...items]

  for (let index = 0; index < updated.length; index += 1) {
    let best = updated[index]
    let bestDistance = Number.POSITIVE_INFINITY

    for (let targetIndex = 0; targetIndex < updated.length; targetIndex += 1) {
      if (targetIndex === index) {
        continue
      }

      const targetItem = updated[targetIndex]
      const desired = (best.size + targetItem.size) / 2 + gap
      const direction = normalized(targetItem.center, best.center)
      const candidate = {
        x: targetItem.center.x + direction.x * desired,
        y: targetItem.center.y + direction.y * desired,
      }

      if (!contains(bounds, candidate, best.size / 2)) {
        continue
      }

      const collisionFree = updated.every((other, otherIndex) => {
        if (otherIndex === index || otherIndex === targetIndex) {
          return true
        }
        const minDistance = (other.size + best.size) / 2 + gap
        return distance(other.center, candidate) >= minDistance
      })

      if (collisionFree) {
        const shift = distance(best.center, candidate)
        if (shift < bestDistance) {
          bestDistance = shift
          best = { ...best, center: candidate }
        }
      }
    }

    updated[index] = best
  }

  return updated
}

const resolveCollisions = (
  items: BubbleLayoutItem[],
  gap: number,
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
) => {
  const updated = [...items]
  const iterations = 120

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    let moved = false
    for (let i = 0; i < updated.length; i += 1) {
      for (let j = i + 1; j < updated.length; j += 1) {
        const a = updated[i]
        const b = updated[j]
        const minDistance = (a.size + b.size) / 2 + gap
        const actual = distance(a.center, b.center)
        if (actual < minDistance) {
          const direction = normalized(b.center, a.center)
          const overlap = minDistance - actual
          const shift = overlap / 2
          const newA = clampPoint(
            {
              x: a.center.x + direction.x * shift,
              y: a.center.y + direction.y * shift,
            },
            bounds,
            a.size / 2
          )
          const newB = clampPoint(
            {
              x: b.center.x - direction.x * shift,
              y: b.center.y - direction.y * shift,
            },
            bounds,
            b.size / 2
          )

          updated[i] = { ...a, center: newA }
          updated[j] = { ...b, center: newB }
          moved = true
        }
      }
    }
    if (!moved) {
      break
    }
  }

  return updated
}

const clampPoint = (
  point: { x: number; y: number },
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  radius: number
) => ({
  x: Math.min(Math.max(point.x, bounds.minX + radius), bounds.maxX - radius),
  y: Math.min(Math.max(point.y, bounds.minY + radius), bounds.maxY - radius),
})

const contains = (
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  point: { x: number; y: number },
  radius: number
) =>
  point.x - radius >= bounds.minX &&
  point.x + radius <= bounds.maxX &&
  point.y - radius >= bounds.minY &&
  point.y + radius <= bounds.maxY

const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

const normalized = (from: { x: number; y: number }, to: { x: number; y: number }) => {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = Math.max(Math.sqrt(dx * dx + dy * dy), 0.001)
  return { x: dx / length, y: dy / length }
}

const hashString = (value: string) => {
  let hash = 5381
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0
  }
  return hash >>> 0
}

const createRng = (seed: number) => {
  let state = seed || 0xdeadbeef
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 0xffffffff
  }
}

const randBetween = (rng: () => number, min: number, max: number) => {
  if (max <= min) {
    return min
  }
  return min + (max - min) * rng()
}
